import { memo, useEffect, useState } from "react";
import { Circle, HardDrive } from "lucide-react";
import type { BackendStatus, DriveStatus } from "@/types";
import { useAppStore, useDriveStore } from "@/stores";
import { formatTime, getPlatform, isTauri } from "@/utils";
import { cn } from "@/utils";

/**
 * StatusBar
 *
 * The persistent footer strip. Reports backend readiness, runtime environment
 * and a live clock — all in the monospace face so figures stay aligned. This
 * is where ambient system state lives so it never interrupts the main view.
 */
const STATUS_LABEL: Record<BackendStatus, string> = {
  offline: "Offline",
  starting: "Starting",
  ready: "Ready",
  error: "Error",
};

const DRIVE_STATUS_LABEL: Record<DriveStatus, string> = {
  disconnected: "No drive",
  connecting: "Connecting",
  connected: "Drive connected",
  missing: "Drive missing",
  invalid: "Invalid drive",
  syncing: "Syncing",
  updating: "Updating",
};

/** Small status-bar segment reporting the active Seufic Drive's lifecycle
 * state — connected drives get a solid icon, anything else (including the
 * steady-state "no drive, using local storage") a dim one. */
function DriveStatusIndicator() {
  const status = useDriveStore((s) => s.status);
  const activeMountPoint = useDriveStore((s) => s.activeMountPoint);
  const isAttention = status === "missing" || status === "invalid";

  return (
    <span
      className="flex items-center gap-1.5"
      title={activeMountPoint ? `${DRIVE_STATUS_LABEL[status]} — ${activeMountPoint}` : DRIVE_STATUS_LABEL[status]}
    >
      <HardDrive
        size={11}
        className={cn(
          status === "connected" ? "text-text-primary" : isAttention ? "text-text-primary" : "text-text-tertiary",
        )}
        aria-hidden
      />
      <span className="uppercase tracking-caps">{DRIVE_STATUS_LABEL[status]}</span>
    </span>
  );
}

export const StatusBar = memo(function StatusBar() {
  const backendStatus = useAppStore((s) => s.backendStatus);
  const [clock, setClock] = useState(() => formatTime());

  useEffect(() => {
    const id = setInterval(() => setClock(formatTime()), 1000);
    return () => clearInterval(id);
  }, []);

  const env = isTauri() ? "native" : "browser";
  const platform = getPlatform();

  return (
    <footer className="flex h-6 shrink-0 items-center gap-4 border-t border-border bg-bg-base px-3 font-mono text-2xs text-text-tertiary">
      <span className="flex items-center gap-1.5">
        <Circle
          size={6}
          className={cn(
            "fill-current",
            backendStatus === "ready"
              ? "text-text-primary"
              : backendStatus === "error"
                ? "text-text-primary opacity-100"
                : "text-text-tertiary",
          )}
          aria-hidden
        />
        <span className="uppercase tracking-caps">
          {STATUS_LABEL[backendStatus]}
        </span>
      </span>

      <span className="opacity-50" aria-hidden>
        │
      </span>

      <DriveStatusIndicator />

      <span className="opacity-50" aria-hidden>
        │
      </span>

      <span className="uppercase tracking-caps">
        {env} · {platform}
      </span>

      <div className="flex-1" />

      <span className="uppercase tracking-caps">v0.1.0</span>
      <span className="opacity-50" aria-hidden>
        │
      </span>
      <span className="tabular" aria-label="Current time">
        {clock}
      </span>
    </footer>
  );
});
